import type { ReactElement, ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import Seo from "../Seo/SeoComponent";
import JlptNotice from "../JlptNotice/JlptNotice";
import styles from "./creditsPage.module.scss";

// Where each credited source can be found. Every URL here was opened on
// 2026-10-10 (see docs/legal/2026-10-10-remediation-log.md). The keys are the
// tag names used inside the locale strings, e.g. "<ccby>CC BY 4.0</ccby>".
//
// tanos.co.uk (Jonathan Waller's original JLPT site) no longer resolves, so
// the Waller licence points at the Internet Archive copy of the author's own
// "Sharing and copyrights" page instead of the dead address.
export const CREDIT_LINKS: Record<string, string> = {
    jlptsite: "https://www.jlpt.jp/e/",
    kanjiapi: "https://kanjiapi.dev",
    jmdictsimplified: "https://github.com/scriptin/jmdict-simplified",
    jmdictextended: "https://github.com/Bluskyo/JMDict_Extended",
    jlptvocabulary: "https://github.com/Bluskyo/JLPT_Vocabulary",
    ccbysa: "https://creativecommons.org/licenses/by-sa/4.0/",
    ccby: "https://creativecommons.org/licenses/by/4.0/",
    edrdglicence: "https://www.edrdg.org/edrdg/licence.html",
    edrdg: "https://www.edrdg.org/",
    jmdict: "https://www.edrdg.org/wiki/index.php/JMdict-EDICT_Dictionary_Project",
    kanjidic: "https://www.edrdg.org/wiki/index.php/KANJIDIC_Project",
    wallerlicence: "https://web.archive.org/web/20250124120638/http://www.tanos.co.uk/jlpt/sharing/",
    hanziwriterdatajp: "https://github.com/k1LoW/hanzi-writer-data-jp",
    animcjk: "https://github.com/parsimonhi/animCJK",
    makemeahanzi: "https://github.com/skishore/makemeahanzi",
    kanasvgdata: "https://github.com/hy2k/kana-svg-data",
    hanziwriter: "https://github.com/chanind/hanzi-writer",
    arphiclicence: "https://ftp.gnu.org/non-gnu/chinese-fonts-truetype/LICENSE",
    lgpl: "https://www.gnu.org/licenses/lgpl-3.0.en.html",
    twemojipolyfill: "https://github.com/talkjs/country-flag-emoji-polyfill",
    twemoji: "https://github.com/twitter/twemoji#license",
    fontawesome: "https://fontawesome.com",
    falicence: "https://fontawesome.com/license/free",
    ofl: "https://openfontlicense.org/",
    frankfurter: "https://frankfurter.dev/",
    ecbcopyright: "https://www.ecb.europa.eu/services/disclaimer/html/index.en.html",
    ecb: "https://www.ecb.europa.eu/stats/policy_and_exchange_rates/euro_reference_exchange_rates/html/index.en.html",
    cloudtranslation: "https://cloud.google.com/translate",
    googletranslate: "https://translate.google.com/",
    googleattribution: "https://docs.cloud.google.com/translate/attribution",
    mymemory: "https://mymemory.translated.net/",
    cloudvision: "https://cloud.google.com/vision",
    tesseract: "https://github.com/naptha/tesseract.js",
    anthropic: "https://www.anthropic.com/",
    apache: "https://www.apache.org/licenses/LICENSE-2.0",
    mit: "https://opensource.org/license/mit",
    react: "https://react.dev/",
    email: "mailto:admin@teqseq.com",
};

export const CREDIT_ENTRY_IDS = [
    "edrdg",
    "waller",
    "strokes",
    "flags",
    "icons",
    "fonts",
    "rates",
    "translation",
    "recognition",
    "software",
] as const;

// Each entry shows these rows in order, skipping the ones its locale strings leave out.
export const CREDIT_ROW_IDS = ["what", "where", "licence", "credit", "changes"] as const;

function CreditLink({ href, children }: { href: string; children?: ReactNode }) {
    if (href.startsWith("mailto:")) return <a href={href}>{children}</a>;
    return (
        <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
        </a>
    );
}

const linkComponents: Record<string, ReactElement> = Object.fromEntries(
    Object.entries(CREDIT_LINKS).map(([tag, href]) => [tag, <CreditLink key={tag} href={href} />])
);

export default function CreditsPage() {
    const { t, i18n } = useTranslation("creditsPage");
    const isPt = i18n.language === "pt";
    const currentPath = isPt ? "/pt/credits" : "/credits";

    return (
        <div className={styles.page}>
            <Seo
                title={t("seoTitle")}
                description={t("seoDescription")}
                path={currentPath}
                lang={isPt ? "pt-PT" : undefined}
            />
            <Header />

            <main id="main-content" className={styles.main}>
                <div className={styles.container}>
                    <h1 className={styles.title}>{t("title")}</h1>
                    <p className={styles.intro}>{t("intro")}</p>

                    <section className={styles.card} aria-labelledby="credits-jlpt">
                        <h2 id="credits-jlpt" className={styles.cardTitle}>
                            {t("jlpt.heading")}
                        </h2>
                        <JlptNotice variant="full" className={styles.jlptNotice} />
                        <p className={styles.afterNotice}>
                            <Trans t={t} i18nKey="jlpt.official" components={linkComponents} />
                        </p>
                    </section>

                    {CREDIT_ENTRY_IDS.map((id) => (
                        <section key={id} className={styles.card} aria-labelledby={`credits-${id}`}>
                            <h2 id={`credits-${id}`} className={styles.cardTitle}>
                                {t(`entries.${id}.title`)}
                            </h2>
                            <dl className={styles.rows}>
                                {CREDIT_ROW_IDS.filter((row) => i18n.exists(`creditsPage:entries.${id}.${row}`)).map((row) => (
                                    <div key={row} className={styles.row}>
                                        <dt>{t(`labels.${row}`)}</dt>
                                        <dd>
                                            <Trans t={t} i18nKey={`entries.${id}.${row}`} components={linkComponents} />
                                            {id === "translation" && row === "licence" && (
                                                // Google asks for this exact English wording, so it is not translated.
                                                <p lang="en" className={styles.verbatim}>
                                                    {t("entries.translation.disclaimer")}
                                                </p>
                                            )}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </section>
                    ))}

                    <p className={styles.contact}>
                        <Trans t={t} i18nKey="contact" components={linkComponents} />
                    </p>
                </div>
            </main>

            <Footer />
        </div>
    );
}
